/**
 * 1_AsdDDReview.js — MDT Review (step 1 of the Adult Autism Diagnostic Decision)
 *
 * This is the first page of the DD ("MDT Review") module. It is a PURE READ-ONLY
 * aggregation: it persists nothing. Its job is to put the two clinicians'
 * appointment reviews side by side so the multi-disciplinary team can see the
 * full picture before making the diagnostic decision on the next page.
 *
 *   Left column  — "Diagnostic Developmental History" (History + SSI appointment).
 *                  Sourced from assessmentStages.semiStructuredInterview.review.
 *   Right column — "Behavioural Observation" (the OBS appointment).
 *                  Sourced from assessmentStages.observation.review.
 *
 * Above the columns, Supporting Documents shows upstream reports that already
 * have a rendered PDF (History / SSI / OBS) — same panel as Autism PAD / the
 * ADHD MDT supporting-docs pattern.
 *
 * Each column shows, top to bottom: the evidence summary table, the confidence
 * level, the emerging differentials, the emerging formulation, and the
 * confidential case notes. Everything renders through the same shared components
 * the review pages themselves use, but with canEdit=false so they are read-only.
 *
 * Because this page only reads, there is no permission write-gate beyond the
 * read access already enforced by ModularPatientDataContext; the MDT review is
 * visible to anyone who can open the patient.
 */

import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SearchIcon, XIcon } from '@heroicons/react/solid';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import ConfidenceLevelSelector from '../../../components/autismReview/ConfidenceLevelSelector';
import AutismSupportingDocumentsPanel from '../../../components/autismReview/AutismSupportingDocumentsPanel';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import NotInReportNotesPanel from '../../../components/autismReview/NotInReportNotesPanel';
import ObservationEvidenceTables from '../12_observation/components/ObservationEvidenceTables';
import { ADULT_ASD_OBSERVATION_TASKS } from '../12_observation/config/observationTasksConfig';
import SSI_CRITERIA from '../15_adultAsdSSI/config/adultAsdSSIConfig';
import { ASD_CONDITION_LABEL } from '../../../constants/autismConditions';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { auth } from '../../../firebase';
import {
  getAutismDDStepRoutes,
  getCombinedEmergingDifferentials,
  buildAutismDDNavItems,
  getAutismDDRouteForSectionIndex,
} from '../../../utils/autismDDRouting';
import { AUTISM_EVIDENCE_PILL_COLOURS } from '../../../constants/autismEvidenceColors';
import { renderNoteWithQuotes } from '../../../utils/highlightQuotes';

/** Evidence pills — Clear = red, No evidence = green (shared Autism scale). */
const EVIDENCE_PILL = AUTISM_EVIDENCE_PILL_COLOURS;

function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">&mdash;</span>;
  const style = EVIDENCE_PILL[value] || { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    // Fixed width sized to "Partial evidence" so Clear / Partial / No /
    // Inconclusive pills align in a clean column (same idea as OBS ADOS pills).
    <span className={`inline-block w-[8rem] text-center px-2 py-0.5 rounded-lg text-[11px] font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

/**
 * Normalises notes into a flat array of `{ id, text }` objects, filtering out
 * empty entries. Handles both StructuredNotesEditor arrays and legacy strings.
 */
function normaliseNotesArray(notesValue) {
  if (!notesValue) return [];
  if (Array.isArray(notesValue)) {
    return notesValue.filter(n => n && String(n.text || '').trim());
  }
  const trimmed = String(notesValue).trim();
  if (!trimmed) return [];
  return [{ id: 'legacy', text: trimmed }];
}

/**
 * Read-only summary table of the SSI criteria evidence (childhood / adulthood)
 * with magnifying glass detail popups — the same pattern as the SSI Review
 * page, showing notes (with inline quote highlighting) and questions marked
 * as asked.
 * Built from assessmentStages.semiStructuredInterview.data.criteria.
 */
export function SsiEvidenceTable({ criteriaData }) {
  const rows = useMemo(
    () => SSI_CRITERIA.filter(c => c.available).map(c => {
      const d = criteriaData[c.id] || {};
      return {
        id: c.id,
        shortTitle: c.shortTitle,
        childhood: d.evidence_childhood || null,
        adulthood: d.evidence_adulthood || null,
        notesChildhood: normaliseNotesArray(d.notes_childhood),
        notesAdulthood: normaliseNotesArray(d.notes_adulthood),
        askedQuestions: d.asked_questions || [],
      };
    }),
    [criteriaData],
  );

  const [detailPopup, setDetailPopup] = useState(null);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic">No interview evidence recorded.</p>;
  }

  // Typography / padding deliberately match ObservationEvidenceTables'
  // compact (MDT half-column) mode so the two tables sit side by side without
  // one looking larger / denser than the other.
  return (
    <>
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <table className="w-full table-fixed text-left border-collapse">
        <colgroup>
          <col style={{ width: '40%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '30%' }} />
        </colgroup>
        <thead>
          <tr className="bg-primary text-white">
            <th className="px-2 py-2 text-xs font-bold">DSM Criteria</th>
            <th className="px-2 py-2 text-xs font-bold text-center">Childhood</th>
            <th className="px-2 py-2 text-xs font-bold text-center">Adulthood</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const renderStageCell = (evidence, notes, stageLabel) => {
              const hasDetail = notes.length > 0 || row.askedQuestions.length > 0;
              return (
                <td className="px-2 py-2 align-top text-center">
                  <div className="flex items-center justify-center gap-1">
                    <EvidencePill value={evidence} />
                    {hasDetail && (
                      <button
                        onClick={() => setDetailPopup({
                          symptomId: row.id,
                          shortTitle: row.shortTitle,
                          stage: stageLabel,
                          evidence,
                          askedQuestions: row.askedQuestions,
                          notes,
                        })}
                        className="text-gray-400 hover:text-primary transition-colors duration-150"
                        title="View details"
                      >
                        <SearchIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              );
            };

            return (
              <tr key={row.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} border-b border-gray-200 last:border-b-0`}>
                <td className="px-2 py-2 align-top">
                  <p className="text-xs text-gray-900 font-bold leading-snug">{row.id}. {row.shortTitle}</p>
                </td>
                {renderStageCell(row.childhood, row.notesChildhood, 'Childhood')}
                {renderStageCell(row.adulthood, row.notesAdulthood, 'Adulthood')}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* SSI criteria detail popup — notes with inline quote highlighting,
        then questions marked as asked. */}
    {detailPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setDetailPopup(null)}
        />
        <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
          <button
            onClick={() => setDetailPopup(null)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>

          <h2 className="text-base font-bold text-gray-900 mb-0.5">
            {detailPopup.symptomId}. {detailPopup.shortTitle}
          </h2>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-gray-500">{detailPopup.stage}</span>
            <EvidencePill value={detailPopup.evidence} />
          </div>

          {detailPopup.notes.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Notes</h3>
              <ul className="space-y-1.5">
                {detailPopup.notes.map((note) => (
                  <li key={note.id} className="flex items-start gap-2">
                    <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                    <span className="text-sm text-gray-700 leading-relaxed">{renderNoteWithQuotes(note.text)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detailPopup.askedQuestions.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Questions Marked as Asked</h3>
              <div className="flex flex-col gap-1.5">
                {detailPopup.askedQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    className="px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed bg-blue-50 text-blue-700 border border-blue-200"
                  >
                    {q}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes fadeScaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to   { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    )}
    </>
  );
}

/**
 * A labelled row whose heading spans the content, then a grid of content.
 * When `observationEnabled` is false (orgs like LMP that do not run a separate
 * Behavioural Observation appointment) the right-hand Observation column is
 * dropped and the interview column spans the full width, so no empty OBS panels
 * render. Defaults to the two-column layout, so TOC is unaffected.
 */
function ReviewRow({ title, left, right, observationEnabled = true }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">{title}</h2>
      <div className={`grid grid-cols-1 ${observationEnabled ? 'lg:grid-cols-2' : ''} gap-6 items-start`}>
        <div>{left}</div>
        {observationEnabled && <div>{right}</div>}
      </div>
    </div>
  );
}

export default function AsdDDReview() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();

  const {
    header,
    assessmentStages,
    flushStageWrites,
  } = useContext(ModularPatientDataContext);

  // Whether this patient's workflow runs Behavioural Observation. When disabled
  // (e.g. LMP) the MDT Review collapses to a single interview column — there is
  // no Observation appointment to summarise. Absent -> enabled, so TOC keeps the
  // side-by-side layout.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const isObservationEnabled = wf?.isObservationEnabled !== false;

  const ssiReview = assessmentStages?.semiStructuredInterview?.review || {};
  const obsReview = assessmentStages?.observation?.review || {};

  const ssiCriteria = assessmentStages?.semiStructuredInterview?.data?.criteria || {};

  // Observation evidence inputs. `criteriaEvidence` is the v2 Criterion Mapping
  // map (one decision per DSM criterion). When present, ObservationEvidenceTables
  // renders the same SSI-style pill summary as OBS Review & Synthesis; when
  // absent it falls back to the legacy per-task evidence matrix for v1 patients.
  const obsData = assessmentStages?.observation?.data || {};
  const obsSelectedTaskIds = obsData.selectedTaskIds || [];
  const obsAllTaskData = obsData.allTaskData || {};
  const obsAdosData = obsData.adosData || null;
  const obsCriteriaEvidence = obsData.criteriaEvidence || null;
  const obsCriteriaNotes = obsData.criteriaNotes || {};

  // Differentials selections (ASD filtered out defensively on read).
  const ssiConditions = (ssiReview.formulationConditions || []).filter(c => c !== ASD_CONDITION_LABEL);
  const obsConditions = (obsReview.formulationConditions || []).filter(c => c !== ASD_CONDITION_LABEL);

  const union = useMemo(
    () => getCombinedEmergingDifferentials(assessmentStages),
    [assessmentStages],
  );
  const hasOther = !wf.skipAutismDifferentialsDecisions && union.length > 0;

  // Sidebar — MDT Review is index 0.
  const { items: navItems, orderedIds } = useMemo(
    () => buildAutismDDNavItems({ assessmentStages, currentSectionIndex: 0, skipDifferentialsDecisions: wf.skipAutismDifferentialsDecisions }),
    [assessmentStages, wf.skipAutismDifferentialsDecisions],
  );

  const navigateToSection = async (index) => {
    await flushStageWrites('diagnosticDecision');
    navigate(getAutismDDRouteForSectionIndex(patientId, index, hasOther));
  };

  const handleNext = async () => {
    await flushStageWrites('diagnosticDecision');
    navigate(getAutismDDStepRoutes(patientId).autismDecision);
  };

  // PO / Adult Autism Assessment / Diagnostic Decision / <page>. The module name
  // ("Diagnostic Decision") is its own breadcrumb level; here it is the module's
  // landing page (MDT Review) so the module name is plain text and the leaf names
  // the page. Sub-pages link the module name back to this MDT Review landing.
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Decision'],
    ['MDT Review'],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Wizard shell — shared sidebar + scrollable content + fixed footer. */}
      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="mdtReview"
          onSelect={(id) => navigateToSection(orderedIds.indexOf(id))}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}
          <div className="flex-1 overflow-y-auto p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">MDT Review</h1>
            <p className="text-gray-500 text-sm mt-1">
              A side-by-side summary of both appointments to inform the diagnostic decision.
            </p>
          </div>

          {/* Supporting Documents — upstream Autism reports that already have a
              rendered PDF (typically History / SSI / OBS by MDT time). Hidden
              when none are previewable. Same panel as Autism PAD. */}
          <AutismSupportingDocumentsPanel
            patientId={patientId}
            clinicianId={auth.currentUser?.uid}
            className="mb-6"
          />

          {/* Column headers — the Observation column is dropped for workflows
              that do not run a Behavioural Observation appointment (e.g. LMP). */}
          <div className={`grid grid-cols-1 ${isObservationEnabled ? 'lg:grid-cols-2' : ''} gap-6 mb-6`}>
            <div className="rounded-xl bg-primary/10 border border-primary/20 px-4 py-3">
              <h3 className="text-sm font-bold text-primary">Diagnostic Developmental History</h3>
              <p className="text-xs text-gray-500">History &amp; Semi-Structured Interview</p>
            </div>
            {isObservationEnabled && (
              <div className="rounded-xl bg-primary/10 border border-primary/20 px-4 py-3">
                <h3 className="text-sm font-bold text-primary">Behavioural Observation</h3>
                <p className="text-xs text-gray-500">Observation appointment</p>
              </div>
            )}
          </div>

          {/* Evidence summary */}
          <ReviewRow
            observationEnabled={isObservationEnabled}
            title="Evidence Summary"
            left={<SsiEvidenceTable criteriaData={ssiCriteria} />}
            right={
              obsSelectedTaskIds.length > 0 ? (
                <ObservationEvidenceTables
                  tasks={ADULT_ASD_OBSERVATION_TASKS}
                  selectedTaskIds={obsSelectedTaskIds}
                  allTaskData={obsAllTaskData}
                  adosData={obsAdosData}
                  criteriaEvidence={obsCriteriaEvidence}
                  criteriaNotes={obsCriteriaNotes}
                  // Denser DSM table for the half-column. ADOS scores still
                  // open in a dialog so this column stays height-balanced
                  // with History.
                  compact
                />
              ) : (
                <p className="text-sm text-gray-400 italic">No observation evidence recorded.</p>
              )
            }
          />

          {/* Confidence level */}
          <ReviewRow
            observationEnabled={isObservationEnabled}
            title="Confidence Level"
            left={
              <ConfidenceLevelSelector
                confidenceStyle="ternary"
                confidence={ssiReview.confidence ?? null}
                confidenceTernary={ssiReview.confidenceTernary ?? null}
                canEdit={false}
                description="Likelihood the person is autistic, based on the interview."
              />
            }
            right={
              <ConfidenceLevelSelector
                confidenceStyle="ternary"
                confidence={obsReview.confidence ?? null}
                confidenceTernary={obsReview.confidenceTernary ?? null}
                canEdit={false}
                description="Likelihood the person is autistic, based on the observation."
              />
            }
          />

          {/* Emerging differentials */}
          <ReviewRow
            observationEnabled={isObservationEnabled}
            title="Emerging Differentials"
            left={
              <ConditionSelectionPanelTOC
                title="Emerging Differentials"
                variant={wf.skipAutismDifferentialsDecisions ? 'non-report' : 'default'}
                density="compact"
                selectedOptions={ssiConditions}
                isMultiSelect
                isConfirmed
                canEdit={false}
                conditions={{}}
              />
            }
            right={
              <ConditionSelectionPanelTOC
                title="Emerging Differentials"
                variant={wf.skipAutismDifferentialsDecisions ? 'non-report' : 'default'}
                density="compact"
                selectedOptions={obsConditions}
                isMultiSelect
                isConfirmed
                canEdit={false}
                conditions={{}}
              />
            }
          />

          {/* Emerging formulation */}
          <ReviewRow
            observationEnabled={isObservationEnabled}
            title="Emerging Formulation"
            left={
              <NotInReportNotesPanel
                title="Emerging Formulation"
                description="From the interview appointment."
                notes={normaliseNotesValue(ssiReview.emergingFormulation)}
                canEdit={false}
                domainId="ssiEmergingFormulationReadOnly"
              />
            }
            right={
              <NotInReportNotesPanel
                title="Emerging Formulation"
                description="From the observation appointment."
                notes={normaliseNotesValue(obsReview.emergingFormulation)}
                canEdit={false}
                domainId="obsEmergingFormulationReadOnly"
              />
            }
          />

          {/* Confidential case notes */}
          <ReviewRow
            observationEnabled={isObservationEnabled}
            title="Confidential Case Notes"
            left={
              <NotInReportNotesPanel
                title="Confidential Case Notes"
                description="From the interview appointment."
                notes={normaliseNotesValue(ssiReview.formulationNotes)}
                canEdit={false}
                domainId="ssiConfidentialNotesReadOnly"
              />
            }
            right={
              <NotInReportNotesPanel
                title="Confidential Case Notes"
                description="From the observation appointment."
                notes={normaliseNotesValue(obsReview.formulationNotes)}
                canEdit={false}
                domainId="obsConfidentialNotesReadOnly"
              />
            }
          />
          </div>

          <ModularStepFooter
            showBack={false}
            counterText={`1 of ${navItems.length}`}
            onNext={handleNext}
            nextLabel="Next: Decision for Autism"
          />
        </div>
      </div>
    </Layout>
  );
}
